/* Copyright (c) 2025 MOCA Systems, Inc.  All rights reserved. */
import {
	DefaultSplitDataRenderType,
	generateSimplestList,
	Plan,
	SimpleAttributeList,
	SplitData,
} from '@touchplan/common';
/**
 * Interface defining the structure of a Promise Period data object.
 * Promise Periods represent time intervals in project planning where teams commit to completing work.
 * Each period has defined start/end dates and can be locked (finalized) or minimized (collapsed in UI).
 */
export interface IPromisePeriod {
	endDate?: string;
	minimized?: boolean;
	locked?: boolean;
	startDate?: string;
}
/**
 * PromisePeriod data model.
 * Promise Periods are time management objects in TouchPlan that represent commitment intervals
 * where teams promise to complete specific work.
 */
export class PromisePeriod extends SplitData<IPromisePeriod, DefaultSplitDataRenderType> {
	/**
	 * Defines which attributes are allowed to be stored and synchronized with Firebase data
	 */
	protected attributeList: SimpleAttributeList = generateSimplestList([
		`endDate`,
		`minimized`,
		`startDate`,
		`locked`,
	]);
	/**
	 * Creates a new PromisePeriod instance.
	 * @param id Unique identifier for this promise period (typically generated by Firebase)
	 */
	constructor(id: string) {
		super(id, DefaultSplitDataRenderType.database);
	}
	/**
	 * Handles incoming data updates from Firebase database.
	 * @param data Raw promise period data from Firebase
	 */
	public databaseUpdate(data: IPromisePeriod): void {
		let promisePeriod: IPromisePeriod = {} as IPromisePeriod;
		for (const key in data) {
			if (this.attributeList[key]) {
				promisePeriod[key] = data[key];
			}
		}
		super.databaseUpdate(promisePeriod);
	}
}
